import { useEffect, useState } from "react";
import axios from "axios";
import { PlusIcon, ArrowDownTrayIcon, ArchiveBoxIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import SearchBar from "./SearchBar";
import LeadsGrid from "./LeadsGrid";
import Import from "./Import";
import LeadForm from "./LeadForm"; 
import LeadDetail from "./LeadDetail"; 
import ArchiveGrid from "./ArchiveGrid"; 

export default function Leads({ onDelete }) {
  const [leads, setLeads] = useState([]); 
  const [filteredLeads, setFilteredLeads] = useState([]); 
  const [search, setSearch] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null); 
  const [isEditing, setIsEditing] = useState(false); 
  const [showArchives, setShowArchives] = useState(false); 
  const [archivedLeads, setArchivedLeads] = useState([]); 
  const [selectedLeads, setSelectedLeads] = useState([]);

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    console.log("Selected leads:", selectedLeads);
  }, [selectedLeads]);  

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/leads?statut=Actif`);
      setLeads(res.data);
      setFilteredLeads(res.data);
    } catch (err) {
      console.error("Erreur lors de la récupération des leads :", err);
    }
  };

  // ✅ Récupérer les leads archivés
  const fetchArchivedLeads = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/leads?statut=Sans Suite`);
      setArchivedLeads(res.data);
    } catch (err) {
      console.error("Erreur lors de la récupération des leads archivés :", err);
    }
  };

  // ✅ Archiver un lead
  const handleArchiveLead = async (leadId) => {
    try {
      await axios.put(`${BACKEND_URL}/leads/${leadId}`, { statut: "Sans Suite" });
      setLeads(leads.filter((lead) => lead._id !== leadId)); // ✅ Mise à jour locale des leads
      setArchivedLeads([...archivedLeads, leads.find((lead) => lead._id === leadId)]);
    } catch (error) {
      console.error("Erreur lors de l'archivage :", error);
    }
  };

  // ✅ Restaurer un lead
  const handleRestoreLead = async (leadId) => {
    try {
      await axios.put(`${BACKEND_URL}/leads/${leadId}`, { statut: "Actif" });
      setArchivedLeads(archivedLeads.filter((lead) => lead._id !== leadId)); // ✅ Mise à jour locale des archives
      setLeads([...leads, archivedLeads.find((lead) => lead._id === leadId)]);
    } catch (error) {
      console.error("Erreur lors de la restauration :", error);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    if (!value) {
      setFilteredLeads(leads);
      return;
    }

    const lowercasedValue = value.toLowerCase();

    const filtered = leads.filter((lead) =>
      Object.values(lead).some(
        (field) =>
          field &&
          field.toString().toLowerCase().includes(lowercasedValue)
      )
    );

    setFilteredLeads(filtered);
  };

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setIsEditing(true);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedLead(null);
    setIsEditing(false);
  };

  return (
    <div className="p-6">
      {/* Barre d'outils */}
      <div className="flex items-center justify-between mb-4">
        <SearchBar
          search={search}
          onSearch={handleSearch}
          selectedLeads={selectedLeads}
          onTransformer={async () => {
            try {
              await Promise.all(
                selectedLeads.map((id) =>
                  axios.put(`${BACKEND_URL}/leads/${id}`, { statut: "Prospect" })
                )
              );
              setSelectedLeads([]);
              fetchLeads();
            } catch (err) {
              console.error("Erreur lors de la transformation :", err);
            }
          }}
          onArchiver={() => {
            selectedLeads.forEach((id) => handleArchiveLead(id));
            setSelectedLeads([]);
          }}
        />

        {/* Boutons */}
        <div className="flex gap-3">
          <button
            className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition"
            onClick={() => setShowImport(true)}
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            Importer
          </button>
          <button
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            onClick={() => {
              setIsEditing(false);
              setShowForm(true);
            }}
          >
            <PlusIcon className="w-5 h-5" />
            Ajouter
          </button>
          <div className="h-10 w-px bg-gray-300" />
          <button
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition"
            onClick={() => {
              setShowArchives(true);
              fetchArchivedLeads();
            }}
          >
            <ArchiveBoxIcon className="w-5 h-5" />
            Archives
          </button>
        </div>
      </div>

      {/* Data Grid */}
      <LeadsGrid
        leads={filteredLeads}
        selectProspect={setSelectedLead}
        onEdit={handleEditLead}
        refreshLeads={fetchLeads}
        selectedLeads={selectedLeads}
        setSelectedLeads={setSelectedLeads}
      />

      {/* Modale Importation */}
      {showImport && <Import onClose={() => setShowImport(false)} />}

      {/* Modale Formulaire Lead */}
      {showForm && (
        <LeadForm 
          onClose={closeForm} 
          refreshLeads={fetchLeads} 
          lead={isEditing ? selectedLead : null} 
        />
      )}

      {/* Modale Détails Lead */}
      {selectedLead && !isEditing && (
        <LeadDetail 
          lead={selectedLead} 
          onClose={() => setSelectedLead(null)} 
          refreshLeads={fetchLeads} 
        />
      )}

      {/* ✅ Modale Archives */}
      {showArchives && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white p-6 rounded-lg shadow-lg w-[800px] max-h-[80vh] overflow-auto"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Leads Archivés</h2>
              <button onClick={() => setShowArchives(false)} className="text-gray-600 hover:text-gray-900">
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <ArchiveGrid
              open={showArchives}
              onClose={() => setShowArchives(false)}
              leads={archivedLeads}
              onRestore={handleRestoreLead}
            />

          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
